import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

// After a redeploy, a tab left open on the old build references JS chunk
// files (content-hashed) that no longer exist on the server — any lazy
// route it navigates to next throws this. Self-heal with a single reload
// (which fetches the current index.html + current chunk hashes) instead of
// showing the user an error screen for something a refresh fixes for free.
// Guarded by sessionStorage so a chunk that's genuinely broken doesn't loop.
const isChunkLoadError = (error: Error): boolean =>
  /Loading chunk .* failed/i.test(error.message) ||
  /ChunkLoadError/i.test(error.name) ||
  /dynamically imported module/i.test(error.message);

const RELOAD_FLAG = 'cedotrace_chunk_reload_attempted';

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary]', error, info.componentStack);

    if (isChunkLoadError(error) && !sessionStorage.getItem(RELOAD_FLAG)) {
      sessionStorage.setItem(RELOAD_FLAG, '1');
      window.location.reload();
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
            <div className="text-5xl mb-4">⚠️</div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">Une erreur est survenue</h2>
            <p className="text-gray-500 text-sm mb-6">
              {this.state.error?.message || 'Erreur inattendue de l\'application.'}
            </p>
            <button
              onClick={() => {
                this.setState({ hasError: false, error: null });
                window.location.reload();
              }}
              className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium"
            >
              Recharger la page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
